import React, { forwardRef } from "react";

import { bridge } from "@multiweb/reactCore/bridge";
import { bemQANames, combineRefs, useStopPropagationEventListenerRef } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { ICryptographicInfo } from "@multiweb/types/multiweb";
import { ForwardRefType } from "@multiweb/types/react";
import { ButtonBackgroundColor, ButtonProvider } from "../ButtonProvider";
import { Icon, IconColor } from "../Icon";

const { isSigned, isEncrypted, hasSignatureWarning } = bridge;

const qa = bemQANames("crypto-info-button");
const qaMain = qa();
const qaSignatureIcon = qa("signature-icon");
const qaWarningIcon = qa("warning-icon");

export type CryptoInfoButtonProps = {
    cryptoInfo?: ICryptographicInfo;
    onClick?: EventListener;
    title?: string;
};

export const CryptoInfoButton: ForwardRefType<HTMLButtonElement, CryptoInfoButtonProps> = forwardRef<HTMLButtonElement, CryptoInfoButtonProps>(({
    cryptoInfo,
    onClick,
    title,
}, forwardRef) => {
    const clickListenerRef = useStopPropagationEventListenerRef({
        eventType: "click",
        callback: onClick,
        dontTriggerCancelEvent: true,
    });

    const ref = combineRefs<HTMLButtonElement>(clickListenerRef, forwardRef);
    const signatureInfo = cryptoInfo?.signatureInfo;
    const hasCockade = isSigned(signatureInfo) || isEncrypted(cryptoInfo);

    if (!hasCockade) {
        return null;
    }

    const hasWarning = hasSignatureWarning(signatureInfo);
    return (
        <ButtonProvider qa={qaMain} ref={ref} onClick={() => null} backgroundColor={ButtonBackgroundColor.transparent} title={title}>
            <Icon qa={qaSignatureIcon} iconCode={IconCode.sportBadgeFilled} color={IconColor.colorPrimary} />
            {
                hasWarning &&
                <Icon qa={qaWarningIcon} iconCode={IconCode.webWarningFilled} color={IconColor.orange} />
            }
        </ButtonProvider>
    );
});
